/**
 * English, Filipino and Cebuano (NFR-07). The English text is the key; FIL and CEB hold
 * translations, and anything missing falls back to English. `{name}` placeholders.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { CEB, FIL } from '../i18n';

export type Lang = 'EN' | 'FIL' | 'CEB';
const KEY = 'lguaid.lang';

interface I18n { lang: Lang; setLang: (l: Lang) => void; t: (en: string, vars?: Record<string, string | number>) => string }
const Ctx = createContext<I18n | null>(null);

function initial(): Lang {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'EN' || v === 'FIL' || v === 'CEB') return v;
  } catch { /* private mode */ }
  const nav = navigator.language?.toLowerCase() ?? '';
  return nav.startsWith('ceb') ? 'CEB' : nav.startsWith('fil') || nav.startsWith('tl') ? 'FIL' : 'EN';
}

export function I18nProvider({ children }: { children: ReactNode }): JSX.Element {
  const [lang, setLangState] = useState<Lang>(initial);
  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try { localStorage.setItem(KEY, l); } catch { /* ignore */ }
    document.documentElement.lang = l === 'FIL' ? 'fil' : l === 'CEB' ? 'ceb' : 'en';
  }, []);
  const t = useCallback((en: string, vars?: Record<string, string | number>) => {
    let s = lang === 'FIL' ? FIL[en] ?? en : lang === 'CEB' ? CEB[en] ?? en : en;
    if (vars) for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`{${k}}`, String(v));
    return s;
  }, [lang]);
  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18n {
  const v = useContext(Ctx);
  if (!v) throw new Error('useI18n outside I18nProvider');
  return v;
}

/** Pick the right field of a bilingual record: name(cat) → cat.nameFil or cat.nameEn. */
export function useLocal() {
  const { lang } = useI18n();
  return <T extends object>(row: T, base: string): string => {
    const r = row as Record<string, unknown>;
    return String((lang === 'FIL' ? r[`${base}Fil`] : undefined) || r[`${base}En`] || '');
  };
}
